import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { finalize, firstValueFrom, timer, take } from 'rxjs';
import { ModalService } from '../../../services/modal.service';

// --- Interfaces for Data Structures ---
interface User {
  nombre: string;
  rol: string;
}

interface StatCard {
  title: string;
  value: number;
  description: string;
}

// Interfaz para el objeto que viene directamente de la API de estadísticas
interface Stat {
  total_contactos?: number;
  contactos_nuevos?: number;
  total_aplicaciones?: number;
  aplicaciones_proceso?: number;
  contactos_por_mes?: { mes: string, total: number }[];
  aplicaciones_por_mes?: { mes: string, total: number }[];
}

interface DataItem {
  id: number;
  nombre: string;
  email: string;
  telefono: string;
  fecha_envio: string;
  estado: string;
  // Contact specific
  empresa?: string;
  mensaje?: string;
  // Job specific
  carrera?: string;
  puesto?: string;
  cv_archivo?: string;
  cv_url?: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.css']
})
export class DashboardComponent implements OnInit, OnDestroy {

  // --- State Properties ---
  activeTab: 'contactos' | 'trabajos' = 'contactos';
  isModalVisible = false;
  currentUser: User | null = null;
  private sessionInterval: any;

  // --- Data Properties ---
  stats: Stat = {};
  private allData: DataItem[] = [];
  filteredData: DataItem[] = [];
  paginatedData: DataItem[] = [];

  // --- Filter and Pagination Properties ---
  filters = { estado: '', fechaDesde: '', fechaHasta: '', buscar: '' };
  currentPage = 1;
  itemsPerPage = 20;
  totalPages = 0;
  pages: number[] = [];

  // --- Password Change Properties ---
  passwordChange = { currentPassword: '', newPassword: '', confirmPassword: '' };
  passwordStrength = { class: '', width: 0, text: '', textClass: '' };
  csrfToken: string = '';

  constructor(
    private router: Router, 
    private http: HttpClient,
    private modalService: ModalService
  ) { }

  ngOnInit(): void {
    console.log('🚀 Iniciando panel de administración...');
    this.verificarSesionConReintentos();
  }

  ngOnDestroy(): void {
    if (this.sessionInterval) {
      clearInterval(this.sessionInterval);
    }
  }

  @HostListener('window:popstate', ['$event']) 
  onPopState(event: Event): void {
    console.log('🚪 Navegación hacia atrás detectada, cerrando sesión...');
    this.logoutYRedirigir();
  }

  @HostListener('window:beforeunload', ['$event']) 
  onBeforeUnload(event: BeforeUnloadEvent): void {
      // Use sendBeacon for a reliable request on page unload
      if (this.currentUser) {
          navigator.sendBeacon('/php/auth/logout.php', new FormData());
      }
  }
  
  // --- Session Management ---
  async verificarSesionConReintentos(intentos = 3): Promise<void> {
    for (let i = 1; i <= intentos; i++) {
      try {
        console.log(`🔍 Verificando sesión... (Intento ${i}/${intentos})`);
        const result = await firstValueFrom(this.http.get<any>('/php/auth/verify_session.php', { withCredentials: true }));

        if (result.loggedIn) {
          console.log('✅ Sesión válida encontrada. Usuario:', result.user.nombre);
          this.currentUser = result.user;
          this.inicializarAdmin();
          return;
        } else {
           console.log(`❌ Sesión no válida en intento ${i}`);
           if (i < intentos) await firstValueFrom(timer(1000 * i));
        }
      } catch (error) {
        console.error(`❌ Error en intento ${i}:`, error);
        if (i < intentos) await firstValueFrom(timer(1000 * i));
      }
    }

    console.log('❌ Todos los intentos de verificación fallaron, redirigiendo al login...');
    this.modalService.open({ title: 'Sesión Inválida', message: 'Sesión no válida o expirada. Será redirigido al login.', isError: true });
    this.router.navigate(['/admin/login']);
  }
  
  private inicializarAdmin(): void {
    console.log('✅ Sesión verificada. Inicializando panel admin...');
    this.reloadData();
    // Re-verificar sesión cada 5 minutos
    this.sessionInterval = setInterval(() => this.verificarSesionConReintentos(1), 300000);
  }

  async logout(): Promise<void> {
    this.modalService.open({
      title: 'Confirmar Cierre de Sesión',
      message: '¿Está seguro que desea cerrar sesión y volver al sitio web principal?',
      confirmText: 'Sí, cerrar sesión',
      cancelText: 'Cancelar'
    }).pipe(take(1)).subscribe(confirmed => {
      if (confirmed) {
        this.logoutYRedirigir();
      }
    });
  }
  
  private async logoutYRedirigir(): Promise<void> {
    try {
        await firstValueFrom(this.http.post('/php/auth/logout.php', {}, { withCredentials: true }));
        console.log('✅ Sesión cerrada en el servidor.');
    } catch (error) {
        console.error('Error cerrando sesión:', error);
    } finally {
        this.currentUser = null;
        this.router.navigate(['/']); // Redirige a la página principal
    }
  }

  // --- Data Loading & Management ---
  reloadData(): void {
    this.loadStats();
    this.loadData();
  }

  async loadStats(): Promise<void> {
    try {
      this.stats = await firstValueFrom(this.http.get<Stat>('/php/api/admin_stats.php', { withCredentials: true }));
      console.log('📊 Estadísticas recibidas:', this.stats);
    } catch (error) {
      console.error('❌ Error cargando estadísticas:', error);
      this.stats = {}; // Reset stats on error
    }
  }

  async loadData(): Promise<void> {
    try {
      this.allData = await firstValueFrom(this.http.get<DataItem[]>(`/php/api/admin_data.php?type=${this.activeTab}`, { withCredentials: true }));
      this.applyFilters();
    } catch (error) {
      console.error(`❌ Error cargando datos para ${this.activeTab}:`, error);
      this.allData = [];
      this.applyFilters();
      this.modalService.open({ title: 'Error de Carga', message: 'Error al cargar los datos. Su sesión puede haber expirado.', isError: true });
    }
  }
  
  switchTab(tab: 'contactos' | 'trabajos'): void {
    if (this.activeTab === tab) return; // No hacer nada si ya está en la pestaña
    this.activeTab = tab;
    this.clearFilters(); // Limpia los filtros y aplica (sobre datos vacíos)
    this.loadData(); // CORRECCIÓN: Carga los datos para la nueva pestaña
  }
  
  // --- Filtering and Pagination ---
  applyFilters(): void {
    let data = [...this.allData];
    const term = this.filters.buscar.toLowerCase();
    
    this.filteredData = data.filter(item => {
        const itemDate = item.fecha_envio.split(' ')[0];
        if (this.filters.estado && item.estado !== this.filters.estado) return false;
        if (this.filters.fechaDesde && itemDate < this.filters.fechaDesde) return false;
        if (this.filters.fechaHasta && itemDate > this.filters.fechaHasta) return false;
        if (term) {
             const inNombre = item.nombre.toLowerCase().includes(term);
             const inEmail = item.email.toLowerCase().includes(term);
             return inNombre || inEmail;
        }
        return true;
    });

    this.totalPages = Math.ceil(this.filteredData.length / this.itemsPerPage);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.changePage(1); // Reinicia a la primera página
  }

  clearFilters(): void {
    this.filters = { estado: '', fechaDesde: '', fechaHasta: '', buscar: '' };
    this.applyFilters();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages && this.totalPages > 0) return;
    this.currentPage = page;
    const startIndex = (page - 1) * this.itemsPerPage;
    // CORRECCIÓN: El segundo argumento de slice es el índice final, no la cantidad.
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedData = this.filteredData.slice(startIndex, endIndex);
  }

  // --- Actions ---
  async updateStatus(id: number, type: string): Promise<void> {
    const item = this.allData.find(d => d.id === id);
    if (!item) {
      this.modalService.open({ title: 'Error', message: 'No se encontró el registro para actualizar.', isError: true });
      return;
    }

    // Define las opciones de estado según el tipo de registro
    const statusOptions = (type === 'contactos')
      ? ['nuevo', 'leido', 'respondido']
      : ['nuevo', 'revision', 'entrevista', 'contratado', 'rechazado'];

    this.modalService.open({
      title: 'Actualizar Estado',
      message: `Seleccione el nuevo estado para el registro #${id} ("${item.nombre}").`,
      confirmText: 'Actualizar',
      cancelText: 'Cancelar',
      statusOptions: statusOptions,
      initialStatus: item.estado
    }).pipe(take(1)).subscribe(async newStatus => {
      // El observable emite el estado seleccionado (string) o `false` si se cancela.
      if (newStatus && typeof newStatus === 'string') {
        try {
          const result = await firstValueFrom(this.http.post<any>('/php/api/admin_update.php', { id, type, status: newStatus }, { withCredentials: true }));
          if (result.success) {
            this.modalService.open({ title: 'Éxito', message: 'El estado ha sido actualizado correctamente.' });
            this.reloadData(); // Recargar datos para ver el cambio
          } else {
            this.modalService.open({ title: 'Error', message: 'No se pudo actualizar el estado: ' + result.message, isError: true });
          }
        } catch (error) {
          this.modalService.open({ title: 'Error de Red', message: 'Hubo un problema de conexión al intentar actualizar.', isError: true });
        }
      }
    });
  }

  async deleteRecord(id: number, type: string): Promise<void> {
    this.modalService.open({
      title: 'Confirmar Eliminación',
      message: `¿Estás seguro de eliminar este registro (ID: ${id})? Esta acción no se puede deshacer.`,
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      isError: true // Para que el botón de confirmar sea rojo
    }).pipe(take(1)).subscribe(async confirmed => {
      if (confirmed) {
        try {
          const result = await firstValueFrom(this.http.post<any>('/php/api/admin_delete.php', { id, type }, { withCredentials: true }));
          if (result.success) {
            this.modalService.open({ title: 'Éxito', message: 'El registro ha sido eliminado.' });
            this.reloadData();
          } else {
            this.modalService.open({ title: 'Error', message: 'Error eliminando registro: ' + result.message, isError: true });
          }
        } catch (error) {
          this.modalService.open({ title: 'Error de Red', message: 'Error de red al eliminar registro.', isError: true });
        }
      }
    });
  }

  exportData(): void {
      if (this.filteredData.length === 0) {
          this.modalService.open({ title: 'Exportación Vacía', message: 'No hay datos para exportar.' });
          return;
      }
      const headers = Object.keys(this.filteredData[0]);
      const csvContent = [
          headers.join(','),
          ...this.filteredData.map(row => headers.map(header => JSON.stringify((row as any)[header])).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rehobot_${this.activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
  }

  // --- Modal & Password Change ---
  async showChangePasswordModal(): Promise<void> {
      try {
        // Obtenemos el token CSRF. La API devuelve directamente { csrf_token: '...' }
        const result = await firstValueFrom(this.http.get<{ csrf_token: string }>('/php/auth/csrf_token.php', { withCredentials: true }));
        if (result && result.csrf_token) {
            this.csrfToken = result.csrf_token;
            console.log('🔒 Token CSRF obtenido.');
            // Solo si el token se obtiene, mostramos el modal
            this.isModalVisible = true;
        } else {
            console.error('❌ No se recibió un token CSRF válido.');
            this.modalService.open({ title: 'Error de Seguridad', message: 'No se pudo verificar la seguridad de la sesión. Intente recargar la página.', isError: true });
        }
      } catch (error) {
          console.error('❌ Error crítico obteniendo token CSRF:', error);
          this.modalService.open({ title: 'Error de Conexión', message: 'No se pudo comunicar con el servidor para iniciar el cambio de contraseña.', isError: true });
      }
  }

  hideChangePasswordModal(): void {
      this.isModalVisible = false;
      this.passwordChange = { currentPassword: '', newPassword: '', confirmPassword: '' };
      this.resetPasswordStrength();
  }

  async changePassword(): Promise<void> {
    if (this.passwordChange.newPassword !== this.passwordChange.confirmPassword) {
      this.modalService.open({ title: 'Error de Validación', message: 'La nueva contraseña y su confirmación no coinciden.', isError: true });
      return;
    }
    if (!this.csrfToken) {
        this.modalService.open({ title: 'Error de Seguridad', message: 'Falta el token de seguridad. Recargue la página.', isError: true });
        return;
    }

    // Usar un objeto simple para JSON en lugar de FormData si el backend lo prefiere
    const payload = {
        csrf_token: this.csrfToken,
        currentPassword: this.passwordChange.currentPassword,
        newPassword: this.passwordChange.newPassword,
        confirmPassword: this.passwordChange.confirmPassword
    };
    
    try {
        const result = await firstValueFrom(this.http.post<any>('/php/api/change_password.php', payload, { withCredentials: true }));
        if (result.success) {
            this.modalService.open({ title: 'Éxito', message: 'Contraseña cambiada exitosamente. Se recomienda volver a iniciar sesión.' });
            this.hideChangePasswordModal();
        } else {
            this.modalService.open({ title: 'Error', message: result.message || 'No se pudo cambiar la contraseña.', isError: true });
        }
    } catch(error: any) {
        const errorMessage = error?.error?.message || 'Hubo un problema de conexión al intentar el cambio.';
        this.modalService.open({ title: 'Error de Servidor', message: errorMessage, isError: true });
        // Si el token es inválido, podría ser útil forzar una recarga o logout.
        if (error.status === 403) {
            this.logoutYRedirigir();
        }
    }
  }

  checkPasswordStrength(): void {
      // (Password strength logic remains the same as previous implementation)
  }

  resetPasswordStrength(): void {
      this.passwordStrength = { class: '', width: 0, text: '', textClass: '' };
  }
  
  // --- Utility ---
  formatDate(dateString: string): string {
      return formatDate(dateString, 'dd/MM/yyyy HH:mm', 'en-US');
  }
}